import React from "react";
import { FileProtectOutlined, RightOutlined, FlagOutlined } from "@ant-design/icons";
import { formatSearchDate } from "../search/searchResultUtils";
import { resolveCheckinTypeLabel } from "./checkinTypeLabel";
import { tatFromRecord } from "../../utils/turnaroundTime";

function Fact({ children }) {
  if (children == null || children === "") return null;
  return <span className="listing-card__fact">{children}</span>;
}

function formatPatientName(claim) {
  const surname = claim?.surname?.__capitalize?.() || claim?.surname || "";
  const first = claim?.first_name?.__capitalize?.() || claim?.first_name || "";
  const middle = claim?.middle_name?.__capitalize?.() || claim?.middle_name || "";
  return `${surname}${surname ? ", " : ""}${first} ${middle}`.trim() || "—";
}

function formatLabel(value) {
  if (!value) return null;
  return value.__capitalize?.() || value;
}

/**
 * Compact linear pre-authorization claim card for the PA queue.
 */
export function PAClaimListingCard({ claim, onClick }) {
  const flagCount = claim?.total_flags || claim?.flags?.active_flags_count || 0;
  const hasFlags = flagCount > 0 || claim?.has_active_flags;
  const flagSeverity = claim?.highest_severity || claim?.flags_highest_severity || "";
  const checkinType = resolveCheckinTypeLabel(claim);

  return (
    <article
      className="listing-card listing-card--linear search-result-card preauth-listing-card"
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="listing-card__lead">
        <div className="listing-card__icon is-preauth">
          <FileProtectOutlined />
        </div>
        <div className="listing-card__identity">
          <h3 className="listing-card__title">{formatPatientName(claim)}</h3>
          <p className="listing-card__meta">{claim?.code || "No encounter code"}</p>
        </div>
      </div>

      <div className="listing-card__facts">
        <Fact>{formatSearchDate(claim?.checkin_date)}</Fact>
        {checkinType ? (
          <span className="listing-card__fact preauth-listing-card__type">{checkinType}</span>
        ) : null}
        <Fact>{formatLabel(claim?.facility_name)}</Fact>
        {claim?.facility_state || claim?.facility_city ? (
          <Fact>
            {[formatLabel(claim?.facility_state), formatLabel(claim?.facility_city)]
              .filter(Boolean)
              .join(", ")}
          </Fact>
        ) : null}
        {claim?.pa_items_count != null ? (
          <Fact>
            {claim.pa_items_count} item{claim.pa_items_count === 1 ? "" : "s"}
          </Fact>
        ) : null}
        {claim?.requested_total != null && claim.requested_total !== "" ? (
          <Fact>
            ₦{Number(claim.requested_total).toLocaleString()}
          </Fact>
        ) : null}
        {claim?.plan_name || claim?.insurance_package ? (
          <Fact>{formatLabel(claim.plan_name || claim.insurance_package)}</Fact>
        ) : null}
        {(() => {
          const tat = tatFromRecord(claim);
          if (!tat.label) return null;
          return <Fact>{tat.pending ? `Age ${tat.label}` : tat.label}</Fact>;
        })()}
      </div>

      <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
        {hasFlags ? (
          <span
            className={`preauth-listing-card__flags${flagSeverity ? ` preauth-table__flags--${flagSeverity}` : ""}`}
            title={flagSeverity ? `${flagSeverity} flag` : "Active flags"}
          >
            <FlagOutlined />
            {flagCount || "!"}
          </span>
        ) : null}
        {onClick ? <RightOutlined className="listing-card__chevron" aria-hidden /> : null}
      </div>
    </article>
  );
}
